import { ComponentProps } from "react";
import Button from "src/components/Button";
import Card from "src/components/Card";
import Link from "src/components/Link";
import Logo from "src/components/Logo";
import { useT } from "src/components/hoc/withTranslation";
import LanguageSelector from "src/components/language/LanguageSelector";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import useTheme from "src/hooks/useTheme";
import { cn } from "src/lib/utils";

interface AuthLayoutProps {
	children?: any;
	head?: {
		title?: string;
		description?: string;
		back?: {
			to?:
				| string
				| {
						modal?: string;
				  };
			label?: string;
		};
	};
	contentClassName?: string;
	variant?: "card" | "flat";
	loading?: boolean;
	actions?: "contact-sales"[];
}

const AuthLayout = ({
	children,
	head,
	variant = "card",
	actions = [],
	contentClassName,
}: AuthLayoutProps) => {
	const { tenant } = useTenant();
	const { theme } = useTheme();
	const t = useT({
		nl: {
			"contact-sales": "Plan een adviesgesprek",
			"part-of": "Onderdeel van GetJobsDone",
		},
		en: {
			"contact-sales": "Schedule an appointment",
			"part-of": "Part of GetJobsDone",
		},
	});
	return (
		<div className="flex flex-col flex-1 bg-gray-background dark:bg-background">
			<div className="py-4 md:py-4 flex mx-auto items-center max-w-xl px-3 md:px-0 w-full">
				<div className="flex md:flex-1"></div>
				<div className="hidden gap-2 md:flex flex-col items-center md:jusitfy-center flex-1">
					<Logo
						variant="default"
						color={theme === "dark" ? "white" : "dark"}
						className="max-h-10 w-full"
					/>
					{tenant?.slug !== "getjobsdone" && (
						<p className="opacity-50 text-[17px]">{t("part-of")}</p>
					)}
				</div>
				<div className="flex gap-2 md:hidden items-center md:jusitfy-center h-8">
					<Logo
						variant="small"
						color={theme === "dark" ? "white" : "dark"}
						className="h-full"
					/>
					{tenant?.slug !== "getjobsdone" && (
						<p className="opacity-50 text-[17px] hidden md:block">
							{t("part-of")}
						</p>
					)}
				</div>
				<div className="flex flex-1 justify-end">
					{actions.includes("contact-sales") && (
						<Button
							type="purple"
							className="bg-purple-light text-purple-dark"
							to="https://calendly.com/kennismaking-getjobsdone/adviesgesprek"
						>
							{t("contact-sales")}
						</Button>
					)}
				</div>
			</div>
			<div className="flex flex-col flex-1 items-center px-3 md:px-0 pt-4 md:pt-16">
				{variant === "card" ? (
					<Card
						className="w-full max-w-[520px]"
						contentClassName={contentClassName}
					>
						{head && (
							<div className="flex flex-col gap-3 mb-4">
								<div className="flex items-center justify-between mb2">
									<h2>{head.title}</h2>
									<LanguageSelector theme={"transparent"} />
								</div>
								{head?.description && <p>{head.description}</p>}
								{head.back?.to && (
									<Link to={head.back.to}>
										<i className="fal fa-arrow-left"></i>
										{head.back?.label && (
											<span className="ml-2">
												{head.back.label}
											</span>
										)}
									</Link>
								)}
							</div>
						)}

						{children}
					</Card>
				) : (
					children
				)}
			</div>
			<div className="h-16 flex"></div>
		</div>
	);
};

export const AuthLayoutContainer = ({
	children,
	className,
	...rest
}: ComponentProps<"div">) => {
	return (
		<div
			{...rest}
			className={cn(
				"md:px-4 md:pt-12 w-full flex flex-col max-w-[1200px]",
				className
			)}
		>
			{children}
		</div>
	);
};

export default AuthLayout;
